import {useEffect, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    Alert,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    TextField
} from "@mui/material";

import {DispatchApi} from "../../core/dispatch/dispatch";
import {useToken} from "../../state/UserContextProvider";
import BigSelector, {BigSelectorItem} from "../../components/common/BigSelector/BigSelector";

type PlaygroundReport = {
    id: string;
    playgroundId: string;
    name: string;
    url: string;
    authToken?: string;
    proxy: boolean;
};

class ReportsDispatchApi extends DispatchApi {
    constructor(token: string | undefined | null) {
        super(token, "reports", false);
    }

    async getReports(playgroundId: string): Promise<PlaygroundReport[]> {
        return this.dispatch("listPlaygroundReports", {playgroundId});
    }

    async createReport(report: Omit<PlaygroundReport, "id">): Promise<PlaygroundReport> {
        return this.dispatch("createPlaygroundReport", report);
    }

    async updateReport(id: string, report: Omit<PlaygroundReport, "id">): Promise<PlaygroundReport> {
        return this.dispatch("updatePlaygroundReport", {id, ...report});
    }

    async deleteReport(id: string): Promise<void> {
        return this.dispatch("deletePlaygroundReport", {id});
    }
}

function useReportsApi() {
    return new ReportsDispatchApi(useToken());
}

const REPORTS_QUERY_KEY = "admin-reports";

type ReportDialogProps = {
    open: boolean;
    onClose: () => void;
    report?: PlaygroundReport;
    playgroundId: string;
};

function ReportDialog({open, onClose, report, playgroundId}: ReportDialogProps) {
    const api = useReportsApi();
    const queryClient = useQueryClient();

    const [name, setName] = useState("");
    const [url, setUrl] = useState("");
    const [authToken, setAuthToken] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [doNotProxy, setDoNotProxy] = useState(false);

    useEffect(() => {
        if (report) {
            setName(report.name);
            setUrl(report.url);
            setAuthToken(report.authToken || "");
            setDoNotProxy(!report.proxy);
        }
    }, [report]);

    const handleSave = async () => {
        if (!name || !url) {
            setError("Name and URL are required");
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const reportData = {
                playgroundId,
                name,
                url,
                authToken: doNotProxy ? undefined : (authToken || undefined),
                proxy: !doNotProxy,
            };

            if (report) {
                await api.updateReport(report.id, reportData);
            } else {
                await api.createReport(reportData);
            }

            await queryClient.invalidateQueries([REPORTS_QUERY_KEY, playgroundId]);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to save report");
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            setName("");
            setUrl("");
            setAuthToken("");
            setDoNotProxy(false);
            setError(null);
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{report ? "Edit Report" : "Create Report"}</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{mb: 2}}>
                        {error}
                    </Alert>
                )}

                <TextField
                    fullWidth
                    label="Report Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={saving}
                    sx={{mt: 2, mb: 2}}
                />

                <TextField
                    fullWidth
                    label="URL"
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    disabled={saving}
                    placeholder="https://example.com/report"
                    sx={{mb: 2}}
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={doNotProxy}
                            onChange={(e) => {
                                setDoNotProxy(e.target.checked);
                                if (e.target.checked) setAuthToken("");
                            }}
                            disabled={saving}
                        />
                    }
                    label="Do Not Proxy"
                    sx={{mb: 1}}
                />

                <TextField
                    fullWidth
                    label="Auth Token (Optional)"
                    value={authToken}
                    onChange={(e) => setAuthToken(e.target.value)}
                    disabled={saving || doNotProxy}
                    type="text"
                    placeholder="Bearer token for authentication"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                    {saving ? <CircularProgress size={20}/> : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

type DeleteConfirmDialogProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    reportName: string;
};

function DeleteConfirmDialog({open, onClose, onConfirm, reportName}: DeleteConfirmDialogProps) {
    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Delete Report</DialogTitle>
            <DialogContent>
                Are you sure you want to delete the report "{reportName}"?
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={onConfirm} color="error" variant="contained">
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );
}

type AdminReportsSectionProps = {
    playgroundId: string;
};

export function AdminReportsSection({playgroundId}: AdminReportsSectionProps) {
    const api = useReportsApi();
    const queryClient = useQueryClient();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingReport, setEditingReport] = useState<PlaygroundReport | undefined>();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingReport, setDeletingReport] = useState<PlaygroundReport | undefined>();

    const {data: reports, isLoading} = useQuery(
        [REPORTS_QUERY_KEY, playgroundId],
        () => api.getReports(playgroundId),
        {staleTime: 30000}
    );

    const deleteMutation = useMutation(
        (reportId: string) => api.deleteReport(reportId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([REPORTS_QUERY_KEY, playgroundId]);
                setDeleteDialogOpen(false);
                setDeletingReport(undefined);
            },
        }
    );

    const handleCreate = () => {
        setEditingReport(undefined);
        setDialogOpen(true);
    };

    const handleEdit = (report: PlaygroundReport) => {
        setEditingReport(report);
        setDialogOpen(true);
    };

    const handleDelete = (report: PlaygroundReport) => {
        setDeletingReport(report);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (deletingReport) {
            deleteMutation.mutate(deletingReport.id);
        }
    };

    const items: BigSelectorItem[] = reports ? reports.map(report => ({
        id: report.id,
        name: report.name,
        subtitle: report.url,
        icon: "fas fa-chart-bar",
        onActivate: () => handleEdit(report),
        deleter: {
            onDelete: () => handleDelete(report),
            confirm: {
                title: "Delete Report",
                message: `Are you sure you want to delete "${report.name}"?`,
            },
        },
    })) : [];

    return (
        <>
            <BigSelector
                title="Custom Reports"
                emptyMessage="No custom reports configured yet."
                loading={isLoading}
                items={items}
                actions={{
                    icon: "fas fa-plus",
                    onClick: handleCreate,
                }}
            />

            <ReportDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                report={editingReport}
                playgroundId={playgroundId}
            />

            {deletingReport && (
                <DeleteConfirmDialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                    onConfirm={handleConfirmDelete}
                    reportName={deletingReport.name}
                />
            )}
        </>
    );
}

